import type { Mask, RgbaImage } from './image';
import type { Rgb } from './color';
import { knockout, type KnockoutParams } from './knockout';
import { hardenAlpha } from './harden';
import { defringe } from './defringe';
import { despeckle, type DespeckleParams } from './despeckle';
import { halftoneAlphaWithMask, type HalftoneParams } from './halftone';
import { trim } from './trim';
import { resizeLanczos } from './resize';
import { inchesToPixels } from './units';
import { ENGINE_VERSION } from './version';

export type StepKind = 'knockout' | 'harden' | 'defringe' | 'despeckle' | 'halftone' | 'trim' | 'resize';
export interface StepBase { id: string; kind: StepKind; enabled: boolean }
export type Step =
  | (StepBase & { kind: 'knockout'; params: KnockoutParams })
  | (StepBase & { kind: 'harden'; params: { threshold: number } })
  | (StepBase & { kind: 'defringe'; params: { radius: number } })
  | (StepBase & { kind: 'despeckle'; params: DespeckleParams })
  | (StepBase & { kind: 'halftone'; params: Omit<HalftoneParams, 'dpi'> })
  | (StepBase & { kind: 'trim'; params: { padding: number } })
  | (StepBase & { kind: 'resize'; params: { widthIn: number; dpi: number } });

export interface PipelineContext {
  dpi: number;
  /** Pixels a despeckle step must keep (see {@link DespeckleParams.protect}). */
  protect?: Mask;
}
export interface StepOutput {
  image: RgbaImage;
  /** The dpi the image carries after this step; only geometry steps set it. */
  dpi?: number;
  info?: Record<string, number>;
  /** Masks in the step's OUTPUT coordinates, for the inspector/canvas overlays. */
  overlays?: Record<string, Mask>;
  warnings?: string[];
}
export interface PipelineResult { image: RgbaImage; dpi: number; warnings: string[] }

const KINDS: StepKind[] = ['knockout', 'harden', 'defringe', 'despeckle', 'halftone', 'trim', 'resize'];

function num(v: unknown, min?: number, max?: number): void {
  if (typeof v !== 'number' || isNaN(v)) throw new Error('not a number');
  if (min !== undefined && v < min) throw new Error(`below ${min}`);
  if (max !== undefined && v > max) throw new Error(`above ${max}`);
}

function positive(v: unknown): void {
  if (typeof v !== 'number' || isNaN(v) || !(v > 0)) throw new Error('must be > 0');
}

function validateParams(kind: StepKind, params: unknown): void {
  if (params === null || Array.isArray(params)) throw new Error('params must be an object');
  if (typeof params !== 'object') throw new Error('params must be an object');
  const p = params as Record<string, unknown>;

  switch (kind) {
    case 'knockout': {
      if (!Array.isArray(p.garment) || p.garment.length !== 3) throw new Error('garment must be array of 3');
      for (const c of p.garment) { if (typeof c !== 'number' || c < 0 || c > 255 || !Number.isInteger(c)) throw new Error('garment values must be integers 0-255'); }
      num(p.tolerance, 0, 255);
      if (p.bake !== undefined && typeof p.bake !== 'boolean') throw new Error('bake must be boolean');
      if (p.edgeConnectedOnly !== undefined && typeof p.edgeConnectedOnly !== 'boolean') throw new Error('edgeConnectedOnly must be boolean');
      if (p.threshold !== undefined) num(p.threshold, 0, 255);
      break;
    }
    case 'harden': {
      num(p.threshold, 0, 255);
      break;
    }
    case 'defringe': {
      num(p.radius, 0);
      break;
    }
    case 'despeckle': {
      num(p.minArea, 0);
      if (p.minSize !== undefined) num(p.minSize, 0);
      if (p.protectDistressed !== undefined && typeof p.protectDistressed !== 'boolean') throw new Error('protectDistressed must be boolean');
      if (p.protectRadius !== undefined) num(p.protectRadius, 0);
      if (p.largeArea !== undefined) num(p.largeArea, 0);
      break;
    }
    case 'halftone': {
      positive(p.lpi);
      if (!Number.isFinite(p.angle)) throw new Error('angle must be finite');
      if (!['round', 'ellipse', 'line', 'square', 'diamond'].includes(p.shape as string)) throw new Error('shape must be round/ellipse/line/square/diamond');
      if (p.minDot !== undefined) num(p.minDot, 0, 1);
      if (p.maxDot !== undefined) num(p.maxDot, 0, 1);
      if (p.gain !== undefined) num(p.gain, 0, 1);
      if (p.onlySoft !== undefined && typeof p.onlySoft !== 'boolean') throw new Error('onlySoft must be boolean');
      break;
    }
    case 'trim': {
      num(p.padding, 0);
      break;
    }
    case 'resize': {
      positive(p.widthIn);
      positive(p.dpi);
      break;
    }
  }
}

export function runStep(img: RgbaImage, step: Step, ctx: PipelineContext): StepOutput {
  switch (step.kind) {
    case 'knockout': return { image: knockout(img, step.params) };
    case 'harden': return { image: hardenAlpha(img, step.params.threshold) };
    case 'defringe': return { image: defringe(img, step.params.radius) };
    case 'despeckle': {
      // ctx.protect is the pipeline's running "do not delete this" mask (halftone
      // dots); an explicit params.protect always wins.
      const params = step.params.protect ? step.params : { ...step.params, protect: ctx.protect };
      const r = despeckle(img, params);
      return { image: r.image, info: { removed: r.removed.length, kept: r.kept.length }, overlays: { candidates: r.candidates } };
    }
    case 'halftone': {
      const r = halftoneAlphaWithMask(img, { ...step.params, dpi: ctx.dpi });
      return { image: r.image, overlays: { halftoned: r.touched } };
    }
    case 'trim': { const r = trim(img, step.params.padding); return { image: r.image, info: { x: r.box.x, y: r.box.y, width: r.box.width, height: r.box.height } }; }
    case 'resize': {
      const w = Math.max(1, Math.floor(inchesToPixels(step.params.widthIn, step.params.dpi)));
      const h = Math.max(1, Math.round((img.height * w) / img.width));
      const scale = w / img.width;
      // resizeLanczos stays soft on purpose: resampling in premultiplied float
      // space is what keeps edges smooth and colours correct, and a hard
      // threshold inside it would alias the very edges it interpolates. But a
      // DTF/screen export must be binary alpha, so the STEP re-hardens the
      // result at the standard 50% threshold; the soft resampler is still
      // available on its own for previews.
      const resized = hardenAlpha(resizeLanczos(img, w, h), 128);
      const out: StepOutput = { image: resized, dpi: step.params.dpi, info: { width: w, height: h, scale } };
      if (scale > 1) {
        out.warnings = [`Upscaling by ${scale.toFixed(2)}×: no detail is added; vendors reject low-resolution files`];
      }
      return out;
    }
    default: { const never: never = step; throw new Error(`unknown step kind ${(never as Step).kind}`); }
  }
}

/** What {@link iteratePipeline} yields after each ENABLED step. */
export interface PipelineStepEvent { index: number; step: Step; output: StepOutput; dpi: number }

/**
 * Run the enabled steps in order, one `next()` at a time, yielding after each
 * one and returning the same {@link PipelineResult} `runPipeline` returns.
 * `ctx` is not mutated: the dpi and the protect mask are carried in a private
 * copy, because both change as steps run and a caller re-running the same
 * pipeline must get the same result.
 *
 * This is the shape a UI needs — it can stop between steps (an abandoned
 * generator simply never runs the rest) or hand the event loop back so a
 * cancel can land — without re-implementing the dpi/protect bookkeeping.
 */
export function* iteratePipeline(img: RgbaImage, steps: Step[], ctx: PipelineContext): Generator<PipelineStepEvent, PipelineResult, void> {
  const state: PipelineContext = { dpi: ctx.dpi, protect: ctx.protect };
  const warnings: string[] = [];
  let cur = img;
  for (let i = 0; i < steps.length; i++) {
    const s = steps[i];
    if (!s.enabled) continue;
    const out = runStep(cur, s, state);
    cur = out.image;
    if (out.dpi !== undefined) state.dpi = out.dpi;
    if (out.warnings) warnings.push(...out.warnings);
    // trim and resize change the pixel grid, so every mask gathered so far is
    // in coordinates that no longer exist: drop the protect mask rather than
    // index a later step into the wrong pixels.
    if (s.kind === 'trim' || s.kind === 'resize') state.protect = undefined;
    else if (out.overlays?.halftoned) state.protect = unionMask(state.protect, out.overlays.halftoned);
    yield { index: i, step: s, output: out, dpi: state.dpi };
  }
  return { image: cur, dpi: state.dpi, warnings };
}

/** Run the whole pipeline to completion, reporting each enabled step to `onStep`. */
export function runPipeline(img: RgbaImage, steps: Step[], ctx: PipelineContext, onStep?: (i: number, out: StepOutput) => void): PipelineResult {
  const it = iteratePipeline(img, steps, ctx);
  for (;;) {
    const r = it.next();
    if (r.done) return r.value;
    onStep?.(r.value.index, r.value.output);
  }
}

function unionMask(a: Mask | undefined, b: Mask): Mask {
  if (!a || a.data.length !== b.data.length) return b;
  const data = new Uint8Array(b.data);
  for (let i = 0; i < data.length; i++) if (a.data[i]) data[i] = 1;
  return { width: b.width, height: b.height, data };
}

/**
 * halftone runs FIRST: it is the only step that reads soft alpha, and
 * knockout/harden leave nothing but 0/255 behind, so screening after them was
 * a no-op. Then knockout (bake + colour knockout), despeckle (which protects
 * the dots halftone just made, via ctx.protect), and trim last.
 */
export function defaultDtfPipeline(garment: Rgb): Step[] {
  return [
    { id: 'halftone', kind: 'halftone', enabled: false, params: { lpi: 30, angle: 22.5, shape: 'round', minDot: 0.02, maxDot: 0.95, gain: 0, onlySoft: true } },
    { id: 'knockout', kind: 'knockout', enabled: true, params: { garment, tolerance: 24, bake: true } },
    { id: 'despeckle', kind: 'despeckle', enabled: true, params: { minArea: 36, protectDistressed: true, protectRadius: 12, largeArea: 400 } },
    { id: 'trim', kind: 'trim', enabled: true, params: { padding: 0 } },
  ];
}

/** A serialised pipeline: the steps plus the engine version that wrote them. */
export interface StepsFile { version: string; steps: Step[] }

export function serializeSteps(steps: Step[]): string {
  return JSON.stringify({ version: ENGINE_VERSION, steps } satisfies StepsFile);
}

export function parseSteps(json: string): Step[] {
  const parsed = JSON.parse(json);
  // Both shapes: the versioned envelope, and a bare array as written before
  // the envelope existed (a saved preset must not stop loading).
  const raw = Array.isArray(parsed)
    ? parsed
    : parsed && typeof parsed === 'object' ? (parsed as { steps?: unknown }).steps : undefined;
  if (!Array.isArray(raw)) throw new Error('steps must be an array');
  const ids = new Set<string>();
  for (const s of raw) {
    if (!s || typeof s !== 'object' || !KINDS.includes(s.kind) || typeof s.id !== 'string' || typeof s.enabled !== 'boolean' || typeof s.params !== 'object') {
      throw new Error(`invalid step ${JSON.stringify(s)}`);
    }
    // Ids address steps in the UI and in per-step overlays, so two steps
    // sharing one are unresolvable, not merely untidy.
    if (ids.has(s.id)) throw new Error(`duplicate step id ${s.id}`);
    ids.add(s.id);
    try {
      validateParams(s.kind, s.params);
    } catch (err) {
      throw new Error(`invalid params for ${s.kind}: ${(err as Error).message}`);
    }
  }
  return raw as Step[];
}
